import { TooltipWithBounds } from '@visx/tooltip';
import { motion } from 'motion/react';
import type { ReactNode } from 'react';
import type { Data, Margin } from '@/types/chart';
import { formatNumberLocale } from '@/utils/helpers/formatters';
import { cn } from '@/utils/ui-utils';

interface TooltipShellProps {
  margin: Margin;
  innerHeight: number;
  tooltipLeft: number | undefined;
  tooltipTop: number | undefined;
  showArrow?: boolean;
  children: ReactNode;
}

const TooltipShell = ({
  margin,
  innerHeight,
  tooltipLeft,
  tooltipTop,
  showArrow = false,
  children,
}: TooltipShellProps) => (
  <>
    <motion.div
      initial={{ height: 0 }}
      animate={{ height: innerHeight + (margin.top || 0) }}
      exit={{ height: 0 }}
      transition={{ duration: 0.3, ease: 'easeInOut' }}
      role='separator'
      aria-orientation='vertical'
      aria-hidden='true'
      className='bg-muted pointer-events-none absolute w-px'
      style={{ top: 0, left: tooltipLeft }}
    />
    <TooltipWithBounds
      left={tooltipLeft}
      top={tooltipTop}
      className={cn(
        `pointer-events-none absolute z-50 flex flex-col rounded-sm bg-black px-3 py-2 text-white
        ${showArrow && 'tooltip-triangle-dark'}`,
      )}
      style={{ backgroundColor: 'black', color: 'white', padding: 12 }}
    >
      {children}
    </TooltipWithBounds>
  </>
);

interface TooltipProps {
  margin: Margin;
  tooltipTop: number | undefined;
  tooltipLeft: number | undefined;
  tooltipData: { data: Data };
  accessor: {
    getValue: (d: Data) => number;
    getDate: (d: Data) => Date;
  };
  innerHeight: number;
  unit?: string;
  showArrow?: boolean;
}

const Tooltip = ({
  margin,
  tooltipTop,
  tooltipLeft,
  tooltipData,
  accessor,
  innerHeight,
  unit,
  showArrow = false,
}: TooltipProps) => (
  <TooltipShell
    margin={margin}
    innerHeight={innerHeight}
    tooltipLeft={tooltipLeft}
    tooltipTop={(tooltipTop || 0) - 100}
    showArrow={showArrow}
  >
    <div className='text-sm leading-4 whitespace-nowrap tabular-nums'>
      {tooltipData.data.value ? (
        <span>
          {formatNumberLocale(accessor.getValue(tooltipData.data))} {unit && unit}
        </span>
      ) : (
        <span className='text-xs whitespace-nowrap text-white/60'>Vi saknar data för perioden.</span>
      )}
    </div>
    <span className='pt-0.5 text-xs whitespace-nowrap text-white/60'>{tooltipData.data.label}</span>

    {tooltipData.data.periodLabel && (
      <span className='pt-0.5 text-xs whitespace-nowrap text-white/60'>{tooltipData.data.periodLabel}</span>
    )}

    {tooltipData.data.details?.map((line, i) => (
      <span
        // biome-ignore lint/suspicious/noArrayIndexKey: detail lines are plain strings with no unique id and can repeat; this is a static per-render tooltip list that isn't reordered.
        key={i}
        className='pt-0.5 text-xs whitespace-nowrap text-white/60'
      >
        {line}
      </span>
    ))}
  </TooltipShell>
);

export { TooltipShell };
export default Tooltip;
